import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EquipmentCard = ({ equipment }) => {
  const { language, getLocalizedLink } = useLanguage();
  const [rentals, setRentals] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [requestType, setRequestType] = useState('specific');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    start_date: '',
    end_date: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Get localized content based on language (name stays in original language)
  const localizedName = equipment.name;
  const localizedSubheading = equipment.subheading;
  // Support both legacy single category and new categories array
  const getLocalizedCategories = () => {
    if (equipment.categories?.length > 0) {
      return equipment.categories.join(', ');
    }
    return language === 'fr' && equipment.category_fr ? equipment.category_fr : equipment.category;
  };
  const localizedCategory = getLocalizedCategories();
  const localizedDescription = language === 'fr' && equipment.description_fr ? equipment.description_fr : equipment.description;

  // Get all images (use images array or fall back to single image_url)
  const images = equipment.images && equipment.images.length > 0 
    ? equipment.images 
    : (equipment.image_url ? [equipment.image_url] : []);

  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  useEffect(() => {
    fetchRentals();
  }, [equipment.id]);

  const fetchRentals = async () => {
    try {
      const res = await axios.get(`${API}/rentals/equipment/${equipment.id}`);
      setRentals(res.data);
    } catch (error) {
      console.error('Error fetching rentals:', error);
    }
  };

  // Get dates that are booked
  const getBlockedDates = () => {
    const blocked = [];
    rentals.forEach(rental => {
      if (rental.start_date && rental.end_date) {
        const start = new Date(rental.start_date);
        const end = new Date(rental.end_date);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          blocked.push(new Date(d));
        }
      }
    });
    return blocked;
  };

  const blockedDates = getBlockedDates();

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/rentals/request`, {
        equipment_id: equipment.id,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        start_date: requestType === 'specific' ? formData.start_date : null,
        end_date: requestType === 'specific' ? formData.end_date : null,
        message: formData.message,
        request_type: requestType
      });

      toast.success(language === 'fr' 
        ? 'Demande envoyée! Nous vous contacterons bientôt.' 
        : 'Request sent! We\'ll contact you soon.');
      setDialogOpen(false);
      setFormData({ customer_name: '', customer_email: '', customer_phone: '', start_date: '', end_date: '', message: '' });
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const labels = {
    en: {
      daily: 'Daily',
      weekly: 'Weekly',
      available: 'Available',
      rented: 'Rented',
      viewCalendar: 'View Availability',
      requestRental: 'Request Rental',
      specificDates: 'Request Specific Dates',
      generalInquiry: 'General Inquiry',
      yourName: 'Your Name',
      email: 'Email',
      phone: 'Phone',
      startDate: 'Start Date',
      endDate: 'End Date',
      message: 'Message / Questions',
      submit: 'Submit Request',
      submitting: 'Sending...',
      availabilityFor: 'Availability for',
      blockedDates: 'Red dates are unavailable',
      close: 'Close',
      photos: 'photos',
      monthly: 'Monthly',
      viewDetails: 'View Details'
    },
    fr: {
      daily: 'Journalier',
      weekly: 'Hebdomadaire',
      available: 'Disponible',
      rented: 'Loué',
      viewCalendar: 'Voir disponibilité',
      requestRental: 'Demande de location',
      specificDates: 'Dates spécifiques',
      generalInquiry: 'Demande générale',
      yourName: 'Votre nom',
      email: 'Courriel',
      phone: 'Téléphone',
      startDate: 'Date de début',
      endDate: 'Date de fin',
      message: 'Message / Questions',
      submit: 'Envoyer la demande',
      submitting: 'Envoi...',
      availabilityFor: 'Disponibilité pour',
      blockedDates: 'Les dates en rouge sont indisponibles',
      close: 'Fermer',
      photos: 'photos',
      monthly: 'Mensuel',
      viewDetails: 'Voir détails'
    }
  };

  const l = labels[language];

  return (
    <>
      <div 
        className="equipment-card bg-card border border-border/60 rounded-lg overflow-hidden"
        data-testid={`equipment-card-${equipment.id}`}
      >
        {/* Image Gallery */}
        <div className="bg-muted">
          {/* Main Image - Links to detail page */}
          <Link to={getLocalizedLink(`/equipment/${equipment.id}`)} className="block aspect-[4/3] overflow-hidden relative group">
            <img 
              src={getImageUrl(images[currentImageIndex])} 
              alt={equipment.name}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              loading="lazy"
              data-testid={`equipment-main-image-${equipment.id}`}
            />
            
            {/* Image Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button 
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); prevImage(); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  data-testid={`equipment-prev-btn-${equipment.id}`}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button 
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); nextImage(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  data-testid={`equipment-next-btn-${equipment.id}`}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>

                {/* Photo Count Badge */}
                <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                  {images.length} {l.photos}
                </div>
                
                {/* View Details hint */}
                <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  {l.viewDetails}
                </div>
              </>
            )}
          </Link>
          
          {/* Thumbnail Strip */}
          {images.length > 1 && (
            <div className="flex gap-1 p-2 bg-muted/80 overflow-x-auto" data-testid={`equipment-thumbnails-${equipment.id}`}>
              {images.map((img, index) => (
                <button
                  key={img}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-14 h-14 rounded overflow-hidden border-2 transition-all ${
                    index === currentImageIndex 
                      ? 'border-primary ring-1 ring-primary' 
                      : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                  data-testid={`equipment-thumb-${equipment.id}-${index}`}
                >
                  <img 
                    src={getImageUrl(img)} 
                    alt={`${localizedName} - ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Clickable content area - links to detail page */}
        <Link to={getLocalizedLink(`/equipment/${equipment.id}`)} className="block p-6 pb-4 hover:bg-muted/30 transition-colors cursor-pointer">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-lg font-medium hover:text-primary transition-colors">{localizedName}</h3>
            <Badge variant={equipment.available ? 'default' : 'secondary'}>
              {equipment.available ? l.available : l.rented}
            </Badge>
          </div>
          
          {/* Subheading */}
          {localizedSubheading && (
            <p className="text-base text-muted-foreground mb-2">
              {localizedSubheading}
            </p>
          )}
          
          <p className="text-sm text-muted-foreground uppercase tracking-wide mb-3">
            {localizedCategory}
          </p>
          
          <p className="text-muted-foreground text-sm mb-4 line-clamp-3 whitespace-pre-line">
            {localizedDescription}
          </p>
          
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">{l.daily}</p>
              <p className="text-lg font-semibold text-primary">${equipment.daily_rate?.toFixed(2)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">{l.weekly}</p>
              <p className="text-lg font-semibold">${equipment.weekly_rate?.toFixed(2)}</p>
            </div>
            {equipment.monthly_rate > 0 && (
              <div className="text-center">
                <p className="text-xs text-muted-foreground">{l.monthly}</p>
                <p className="text-lg font-semibold">${equipment.monthly_rate?.toFixed(2)}</p>
              </div>
            )}
          </div>
        </Link>

        {/* Buttons stay outside the link to remain independently clickable */}
        <div className="flex gap-2 px-6 pb-6">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 rounded-full"
            onClick={() => setCalendarOpen(true)}
          >
            <CalendarIcon className="h-4 w-4 mr-1" /> {l.viewCalendar}
          </Button>
          <Button 
            size="sm" 
            className="flex-1 rounded-full"
            onClick={() => setDialogOpen(true)}
          >
            {l.requestRental}
          </Button>
        </div>
      </div>

      {/* Availability Calendar Dialog */}
      <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{l.availabilityFor} {localizedName}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <Calendar
              mode="multiple"
              selected={blockedDates}
              className="rounded-md border"
              modifiers={{
                blocked: blockedDates
              }}
              modifiersStyles={{
                blocked: { backgroundColor: 'hsl(var(--destructive))', color: 'white', borderRadius: '4px' }
              }}
              disabled={(date) => date < new Date()}
            />
          </div>
          <p className="text-sm text-muted-foreground text-center">{l.blockedDates}</p>
          <Button variant="outline" className="w-full rounded-full mt-2" onClick={() => setCalendarOpen(false)}>
            {l.close}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Rental Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{l.requestRental}: {localizedName}</DialogTitle>
          </DialogHeader>
          
          <div className="flex gap-2 mt-4">
            <Button 
              variant={requestType === 'specific' ? 'default' : 'outline'} 
              size="sm"
              className="flex-1 rounded-full"
              onClick={() => setRequestType('specific')}
            >
              {l.specificDates}
            </Button>
            <Button 
              variant={requestType === 'inquiry' ? 'default' : 'outline'} 
              size="sm"
              className="flex-1 rounded-full"
              onClick={() => setRequestType('inquiry')}
            >
              {l.generalInquiry}
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>{l.yourName} *</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                required
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{l.email} *</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{l.phone}</Label>
                <Input
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {requestType === 'specific' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{l.startDate} *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{l.endDate} *</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    min={formData.start_date || new Date().toISOString().split('T')[0]}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            <div>
              <Label>{l.message}</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                placeholder={requestType === 'inquiry' ? (language === 'fr' ? 'Questions sur cet équipement...' : 'Questions about this equipment...') : ''}
                className="mt-1"
                rows={3}
              />
            </div>

            <Button type="submit" className="w-full rounded-full" disabled={submitting}>
              {submitting ? l.submitting : l.submit}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
